import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { fetchJournalPost } from "../lib/api";
import { ArrowLeft } from "lucide-react";
import SocialShare from "../components/SocialShare";
import PageMeta from "../components/PageMeta";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const MARKDOWN_COMPONENTS = {
  h1: ({ node, ...p }) => (
    <h1 className="font-serif text-3xl md:text-4xl text-ink mt-12 mb-4 tracking-tight" {...p} />
  ),
  h2: ({ node, ...p }) => (
    <h2 className="font-serif text-2xl md:text-3xl text-ink mt-10 mb-3 tracking-tight" {...p} />
  ),
  h3: ({ node, ...p }) => (
    <h3 className="font-serif text-xl md:text-2xl text-ink mt-8 mb-3 tracking-tight" {...p} />
  ),
  p: ({ node, ...p }) => <p className="my-5 leading-[1.8]" {...p} />,
  a: ({ node, ...p }) => (
    <a className="text-river underline underline-offset-4 hover:text-ember" target="_blank" rel="noopener noreferrer" {...p} />
  ),
  blockquote: ({ node, ...p }) => (
    <blockquote className="border-l-4 border-ember pl-6 py-2 my-8 font-serif text-xl md:text-2xl text-ink italic" {...p} />
  ),
  ul: ({ node, ...p }) => <ul className="my-5 ml-6 list-disc space-y-2" {...p} />,
  ol: ({ node, ...p }) => <ol className="my-5 ml-6 list-decimal space-y-2" {...p} />,
  img: ({ node, ...p }) => (
    <img className="my-8 w-full h-auto" alt="" {...p} />
  ),
  hr: () => <hr className="my-12 border-rule" />,
};

export default function JournalPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchJournalPost(slug)
      .then((p) => setPost(p))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-ink-muted font-accent">Loading…</p>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-[60vh] flex items-center" data-testid="journal-post-notfound">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 w-full">
          <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
            Not found
          </div>
          <h1 className="font-serif text-4xl md:text-5xl mt-4 text-ink leading-tight tracking-tight">
            That piece isn&rsquo;t here.
          </h1>
          <p className="mt-5 text-lg text-ink-muted max-w-xl leading-relaxed">
            It may have been moved, or the link may be wrong.
          </p>
          <button
            onClick={() => navigate("/journal")}
            className="mt-8 inline-flex items-center gap-2 bg-river text-linen px-6 py-3 text-sm font-accent font-semibold tracking-wide hover:bg-river-dark transition-colors"
          >
            Back to the journal
          </button>
        </div>
      </div>
    );
  }

  return (
    <article data-testid="journal-post-page" className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
      <PageMeta
        title={post.title}
        description={post.excerpt}
        path={`/journal/${post.slug}`}
        type="article"
        image={post.hero_image_id ? `https://truth4parents.com/api/media/${post.hero_image_id}` : undefined}
        publishedTime={post.created_at}
      />
      <Link
        to="/journal"
        data-testid="journal-back-link"
        className="inline-flex items-center gap-2 text-sm font-accent text-ink-muted hover:text-ink mb-10"
      >
        <ArrowLeft size={14} strokeWidth={1.5} />
        Back to journal
      </Link>

      <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
        {post.tag}
      </div>
      <h1 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-ink mt-5 text-balance leading-[1.05]">
        {post.title}
      </h1>
      <div className="mt-6 text-sm text-ink-muted font-accent">
        {formatDate(post.created_at)}
      </div>

      {post.hero_image_id && (
        <img
          src={`/api/media/${post.hero_image_id}`}
          alt=""
          className="mt-12 w-full h-auto"
        />
      )}

      <p className="mt-10 text-xl md:text-2xl font-serif text-ink leading-snug max-w-3xl text-balance">
        {post.excerpt}
      </p>

      <div className="mt-10 max-w-3xl text-base md:text-lg text-ink-muted font-light leading-[1.8]">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
          {post.body}
        </ReactMarkdown>
      </div>

      <SocialShare title={post.title} slug={post.slug} />
    </article>
  );
}
